/**
 * Componente: PurchaseTable
 * 
 * Tabla principal para mostrar el historial de compras con:
 * - Paginación
 * - Búsqueda
 * - Ordenamiento por columnas
 * - Acciones (ver detalles, descargar factura)
 * 
 * @param {Array} purchases - Array de compras a mostrar
 * @param {Object} pagination - Información de paginación del backend
 * @param {Function} onPageChange - Callback cuando cambia la página
 * @param {Function} onViewDetails - Callback para ver detalles de una compra
 * @param {Function} onDownloadInvoice - Callback para descargar factura
 * @param {boolean} loading - Estado de carga
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, CircularProgress } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import { getPurchaseTableColumns, transformPurchasesForTable } from '../data/purchaseTableData';

function PurchaseTable({
  purchases,
  pagination,
  onPageChange,
  onViewDetails,
  onDownloadInvoice,
  loading,
}) {
  // Columnas de la tabla
  const columns = useMemo(() => getPurchaseTableColumns(), []);

  // Datos transformados para la tabla
  const rows = useMemo(
    () => transformPurchasesForTable(purchases, onViewDetails, onDownloadInvoice),
    [purchases, onViewDetails, onDownloadInvoice]
  );

  // Configuración de la tabla
  const tableData = {
    columns,
    rows,
  };

  if (loading) {
    return (
      <Card sx={{ p: 3 }}>
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress color="info" />
        </MDBox>
      </Card>
    );
  }

  if (!purchases || purchases.length === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <MDBox display="flex" flexDirection="column" alignItems="center" py={5}>
          <MDTypography variant="h6" color="text" mb={1}>
            No se encontraron compras
          </MDTypography>
          <MDTypography variant="button" color="text" fontWeight="regular">
            No hay compras que coincidan con los filtros seleccionados
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card>
      <MDBox p={3}>
        <DataTable
          table={tableData}
          showTotalEntries={true}
          isSorted={true}
          noEndBorder
          entriesPerPage={false}
          canSearch={false}
        />
        
        {/* Paginación personalizada */}
        {pagination && pagination.totalPages > 1 && (
          <MDBox mt={3} display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="button" color="text" fontWeight="regular">
              Mostrando página {pagination.page} de {pagination.totalPages}
              {' '}({pagination.total} registros totales)
            </MDTypography>
            
            <MDBox display="flex" gap={1}>
              <MDBox
                component="button"
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  cursor: pagination.page > 1 ? 'pointer' : 'not-allowed',
                  opacity: pagination.page > 1 ? 1 : 0.5,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: pagination.page > 1 ? 'action.hover' : 'transparent',
                  },
                }}
              >
                <MDTypography variant="button" fontWeight="medium">
                  Anterior
                </MDTypography>
              </MDBox>
              
              <MDBox
                component="button"
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  cursor: pagination.page < pagination.totalPages ? 'pointer' : 'not-allowed',
                  opacity: pagination.page < pagination.totalPages ? 1 : 0.5,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: pagination.page < pagination.totalPages ? 'action.hover' : 'transparent',
                  },
                }}
              >
                <MDTypography variant="button" fontWeight="medium">
                  Siguiente
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

PurchaseTable.propTypes = {
  purchases: PropTypes.arrayOf(PropTypes.object),
  pagination: PropTypes.shape({
    page: PropTypes.number,
    totalPages: PropTypes.number,
    total: PropTypes.number,
    limit: PropTypes.number,
  }),
  onPageChange: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onDownloadInvoice: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

PurchaseTable.defaultProps = {
  purchases: [],
  pagination: null,
  loading: false,
};

export default PurchaseTable;
